import React, { useCallback } from "react";
import Swal from "sweetalert2";
import CloseIcon from "@mui/icons-material/Close";
import defaultAxios, { AxiosError } from "axios";
import useDeleteKategori from "../../services/mutations/useDeleteKategori";
import { LoadingButton } from "@mui/lab";
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Divider,
    IconButton,
    Stack,
    Typography,
    useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

interface IModalDeleteKategoriProps {
    refetch: () => void;
    open: boolean;
    onClose: () => void;
    kategoriName: string;
    kategoriId: number | string | undefined;
    onBackToKategori: () => void;
}

const ModalDeleteKategori = ({
    refetch,
    open,
    onClose,
    kategoriName,
    kategoriId,
    onBackToKategori,
}: IModalDeleteKategoriProps) => {
    const theme = useTheme();
    const isPhoneScreen = useMediaQuery(theme.breakpoints.down("sm"));
    const deleteKategori = useDeleteKategori();

    const handleDelete = (id: string | number | undefined) => {
        if (id === undefined) {
            onClose();
            onBackToKategori();
            return;
        }
        deleteKategori.mutate(id, {
            onSuccess: (data) => {
                if (data.code === 200) {
                    Swal.fire({
                        title:
                            "Kategori " + kategoriName + " berhasil dihapus!",
                        position: "top-end",
                        showConfirmButton: false,
                        icon: "success",
                        toast: true,
                        timer: 3000,
                        timerProgressBar: true,
                        showCloseButton: true,
                        customClass: {
                            container: "my-swal",
                        },
                    });
                    refetch();
                }
                onClose();
                onBackToKategori();
            },
            onError: (error) => {
                handleErrorResponse(error);
            },
        });
    };

    const handleErrorResponse = useCallback(
        (error) => {
            if (defaultAxios.isAxiosError(error)) {
                const serverError = error as AxiosError<any>;
                if (serverError && serverError.response) {
                    Swal.fire({
                        title: "Terjadi Kesalahan!",
                        text: `${serverError.response.data.message}`,
                        icon: "error",
                        confirmButtonColor: "#45A779",
                        customClass: {
                            container: "my-swal",
                        },
                    });
                    onClose();
                } else {
                    Swal.fire({
                        title: "Terjadi Kesalahan!",
                        text: `Telah Terjadi Kesalahan`,
                        icon: "error",
                        confirmButtonColor: "#45A779",
                        customClass: {
                            container: "my-swal",
                        },
                    });
                    onClose();
                }
            }
        },
        [onClose],
    );

    return (
        <Dialog
            maxWidth="xs"
            fullWidth={true}
            fullScreen={isPhoneScreen}
            open={open}
            onClose={onClose}
            PaperProps={{
                sx: { borderRadius: 1 },
            }}
        >
            <DialogTitle>
                <Stack 
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    spacing={1}
                >
                    <Typography variant="h6" fontWeight="bold">
                        Hapus Kategori
                    </Typography>

                    <IconButton
                        onClick={() => {
                            onClose();
                            onBackToKategori();
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </Stack>
            </DialogTitle>

            <Divider />
            
            <DialogContent>
                <DialogContentText>
                    {kategoriName} akan dihapus secara permanen. Yakin ingin
                    menghapus?
                </DialogContentText>
            </DialogContent>
            <DialogActions sx={{ padding: 2 }}>
                
                <Button
                    variant="outlined"
                    onClick={() => {
                        onClose();
                        onBackToKategori();
                    }}
                    color="error"
                >
                    Tidak
                </Button>

                <LoadingButton
                    loading={deleteKategori.isLoading}
                    loadingPosition="center"
                    variant="contained"
                    onClick={() => handleDelete(kategoriId)}
                    color="error"
                >
                    Hapus
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
};

export default ModalDeleteKategori;
